import { restGet } from "@/api/rest";
import { presignAndUpload } from "@/api/upload";
import { processAvatarImage } from "@/lib/frameImage";
import { avatarDisplayUrl, objectUrl } from "@/lib/media";
import { supabase } from "@/lib/supabase";
import type { UserProfile } from "@/lib/types";

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL?.trim() ?? "";
const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY?.trim() ?? "";

function parseFrameMeta(row: Record<string, unknown>): {
  accent: string | null;
  widthDp: number;
} {
  const frame = row.equipped_frame as Record<string, unknown> | null | undefined;
  const meta = frame?.asset_metadata as Record<string, unknown> | undefined;
  const accent =
    typeof meta?.accent === "string" && meta.accent.trim()
      ? meta.accent.trim()
      : null;
  const widthDp =
    meta?.width_dp != null ? Number(meta.width_dp) || 3 : 3;
  return { accent, widthDp };
}

function parseThemeMeta(row: Record<string, unknown>): string[] | null {
  const theme = row.equipped_theme as Record<string, unknown> | null | undefined;
  const meta = theme?.asset_metadata as Record<string, unknown> | undefined;
  const gradient = meta?.gradient;
  if (!Array.isArray(gradient)) return null;
  const colors = gradient
    .map((c) => (typeof c === "string" ? c.trim() : ""))
    .filter((c) => c.length > 0);
  return colors.length >= 2 ? colors : null;
}

function parseBadgeMeta(row: Record<string, unknown>): string | null {
  const badge = row.equipped_badge as Record<string, unknown> | null | undefined;
  const meta = badge?.asset_metadata as Record<string, unknown> | undefined;
  const label = meta?.label;
  return typeof label === "string" && label.trim() ? label.trim() : null;
}

const PROFILE_SELECT =
  "id,username,full_name,avatar_url,bio,website,is_private,home_city,verified_creator,creator_pro_expires_at," +
  "equipped_frame:digital_items!equipped_frame_item_id(asset_metadata)," +
  "equipped_theme:digital_items!equipped_theme_item_id(asset_metadata)," +
  "equipped_badge:digital_items!equipped_badge_item_id(asset_metadata)";

function mapProfile(row: Record<string, unknown>): UserProfile {
  const id = String(row.id ?? "");
  const frame = parseFrameMeta(row);
  return {
    id,
    username: String(row.username ?? ""),
    fullName: String(row.full_name ?? row.username ?? "Daywia user"),
    avatarUrl: avatarDisplayUrl(id, row.avatar_url as string | null),
    bio: String(row.bio ?? ""),
    website: String(row.website ?? ""),
    isPrivate: row.is_private === true || row.is_private === "true",
    homeCity: (row.home_city as string | null) ?? null,
    verifiedCreator:
      row.verified_creator === true || row.verified_creator === "true",
    creatorProExpiresAt: (row.creator_pro_expires_at as string | null) ?? null,
    profileFrameAccent: frame.accent,
    profileFrameWidthDp: frame.widthDp,
    profileThemeGradient: parseThemeMeta(row),
    profileBadgeLabel: parseBadgeMeta(row),
  };
}

export async function fetchMyProfile(userId: string): Promise<UserProfile> {
  const { data, error } = await supabase
    .from("profiles")
    .select(PROFILE_SELECT)
    .eq("id", userId)
    .limit(1)
    .maybeSingle();

  if (error) throw new Error(error.message);
  if (!data) throw new Error("Profile not found");
  return mapProfile(data as unknown as Record<string, unknown>);
}

export async function fetchProfileById(userId: string): Promise<UserProfile> {
  const { data, error } = await supabase
    .from("profiles")
    .select(PROFILE_SELECT)
    .eq("id", userId.trim())
    .limit(1)
    .maybeSingle();

  if (error) throw new Error(error.message);
  if (!data) throw new Error("Profile not found");
  return mapProfile(data as unknown as Record<string, unknown>);
}

export async function checkUsernameAvailable(
  username: string,
  excludeUserId?: string,
): Promise<boolean> {
  const u = username.trim().toLowerCase();
  if (u.length < 3) return false;
  const rows = await restGet<{ id: string }[]>("profiles", {
    select: "id",
    username: `eq.${u}`,
    limit: "1",
  });
  if (rows.length === 0) return true;
  return excludeUserId != null && rows[0].id === excludeUserId;
}

export interface ProfileUpdateInput {
  id: string;
  fullName: string;
  username: string;
  bio: string;
  mobileNumber: string | null;
  isPrivate: boolean;
  avatarUrl: string;
}

export async function updateMyProfile(input: ProfileUpdateInput): Promise<void> {
  const session = await supabase.auth.getSession();
  const token = session.data.session?.access_token;
  if (!token) throw new Error("Not signed in");

  const res = await fetch(`${supabaseUrl}/rest/v1/profiles?on_conflict=id`, {
    method: "POST",
    headers: {
      apikey: anonKey,
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
      Prefer: "resolution=merge-duplicates,return=minimal",
    },
    body: JSON.stringify({
      id: input.id,
      username: input.username.trim().toLowerCase(),
      full_name: input.fullName.trim(),
      avatar_url: input.avatarUrl,
      bio: input.bio.trim() || null,
      mobile_number: input.mobileNumber,
      is_private: input.isPrivate,
    }),
  });

  if (!res.ok) {
    const detail = await res.text().catch(() => "");
    throw new Error(`Profile update failed: ${res.status} ${detail}`);
  }
}

export async function uploadAvatar(userId: string, file: File): Promise<string> {
  const webp = await processAvatarImage(file);
  const path = `users/${userId}/profile/avatar.webp`;
  await presignAndUpload("users", path, webp, "image/webp");
  const base = objectUrl(path);
  return `${base}?v=${Date.now()}`;
}
